import {C} from "./configurations/active-configuration";
import {GROUP_READ_ONLY, GROUP_STAFF, GROUP_SUPER, GROUP_USER} from "./services/ApiTypes";
import type {User} from "./services/classes/User";
import {B} from "./build";
import type {SitRole} from "./sit/ApiTypes";
import Rollbar from "rollbar";
import type {UnderConstructionOptions} from "./utils/underConstructionMode.ts";

const staticIo = {
    inFilename: '20210409 Spend Analysis.xlsx',
    outFilename: '20210409 Spend Analysis_result.xlsx',
    outFileUrl: 'https://storage.googleapis.com/mithra-cat-production-static/demo/20210409%20Spend%20Analysis_result.xlsx',
}

const demoFiles = {
    spendClassificationResult: {
        name: 'LibertyGlobal-Categorization.xlsx',
        url: 'https://storage.googleapis.com/mithra-cat-production-static/demo/LibertyGlobal-Categorization.xlsx',
    },
    supplierHierarchy: {
        name: 'LibertyGlobal-SupplierHierarchy.xlsx',
        url: 'https://storage.googleapis.com/mithra-cat-production-static/demo/LibertyGlobal-SupplierHierarchy.xlsx',
    }
}

export const DEV_USER_DUMMY: User = {
    id: 1,
    username: 'dummy',
    email: 'dummy@mithra-ai.com',
    first_name: 'Demo',
    last_name: 'User',
    groups: [GROUP_USER, GROUP_SUPER, GROUP_STAFF],
    // groups: [GROUP_USER],
    is_staff: true,
    // "id": 17,
    // "username": "dennis4",
    // "email": "dennis@mithra-ai.com",
    // "first_name": "dennis@mithra-ai.com",
    // "last_name": "",
    // "groups": [
    //     "customer_readonly"
    // ],
    // "is_staff": false,
    sit_user_profile: {
        // Change this to 1 in case you want to test SIT initiative lock on dev, set to 2 otherwise
        id: 2,
        email: 'dummy@mithra-ai.com',
        name: 'Demo User',
        first_name: 'Demo',
        last_name: 'User',
        default_company: {
            "id": 1,
            "cluster": {"id": 1, "title": "Cluster 1"},
            "currency": {"id": 5, "name": "Swedish Krona", "code": "SEK"},
            "country": {"id": 5, "name": "Sweden", "code_a3": "SWE"},
            "title": "FFU HS",
            "code": "FFU",
            "fiscal_year_start_month": 8
        },
        companies: [1],
        role: 'admin' as SitRole,
        created: '09-25T10:58:56.794736+02:00',
    }
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _x = [GROUP_READ_ONLY, GROUP_STAFF, GROUP_SUPER, GROUP_USER];

type FirebaseConfig = {
    apiKey: string,
    authDomain: string,
    projectId: string,
    storageBucket: string,
    messagingSenderId: string,
    appId: string,
    measurementId: string,
}
const firebaseConfig: FirebaseConfig = {
    apiKey: B.FIREBASE_API_KEY,
    authDomain: B.FIREBASE_AUTH_DOMAIN,
    projectId: B.FIREBASE_PROJECT_ID,
    storageBucket: B.FIREBASE_STORAGE_BUCKET,
    messagingSenderId: B.FIREBASE_MESSAGING_SENDER_ID,
    appId: B.FIREBASE_APP_ID,
    measurementId: B.FIREBASE_MEASUREMENT_ID,
}
if (!firebaseConfig.authDomain && firebaseConfig.projectId) {
    firebaseConfig.authDomain = `${firebaseConfig.projectId}.firebaseapp.com`;
}
if (!firebaseConfig.storageBucket && firebaseConfig.projectId) {
    firebaseConfig.storageBucket = `${firebaseConfig.projectId}.appspot.com`;
}

export type PingConfig = {
    maxRetries: number,
    retryInterval: number,
    keepAliveInterval: number,
}

const PING_BEHAVIORS = {
    'default': {
        maxRetries: 100, // max 15 minutes
        retryInterval: 10 * 1000,
        keepAliveInterval: 5 * 1000,
    },
    'conservative': {
        maxRetries: 5,
        retryInterval: 30 * 1000,
        keepAliveInterval: 55 * 1000,
    },
}

export const environment = {
    environmentName: B.APP_ENVIRONMENT,
    /**
     * TODO: CAT-712: Remove customer names from public source code
     * @deprecated
     */
    customerName: B.APP_CUSTOMER,
    locale: undefined as (string | undefined), // https://momentjs.com/ (en-GB, en-US, nl-NL)
    production: !B.APP_IS_DEV,
    testAuthentication: B.APP_ENVIRONMENT.endsWith('auth'),
    isStaging: B.APP_ENVIRONMENT.includes('staging'),
    isTest: false,
    isTestChat: false,
    firebasePreviewConfiguration: B.PREVIEW_ID,
    showDebugInfoInMenu: B.APP_IS_DEV,
    devMenu: false,
    devLogin: B.APP_IS_DEV, // Automatically login with DEV_USER_ID
    /** MSW API mocking in the browser (dev only). Set `VITE_ENABLE_MSW=true` in `.environments/.env.local`. */
    enableMsw: B.APP_IS_DEV && import.meta.env.VITE_ENABLE_MSW === 'true',
    appEnginePinging: PING_BEHAVIORS[B.APP_PING_INTERVAL || 'default'] as PingConfig,
    rollbarConfig: (B.APP_ROLLBAR_ACCESS_TOKEN && !B.APP_IS_DEV) ? {
        accessToken: B.APP_ROLLBAR_ACCESS_TOKEN,
        captureUncaught: true,
        captureUnhandledRejections: true,
        payload: {
            environment: B.APP_ENVIRONMENT,
            client: {
                javascript: {
                    code_version: B.APP_VERSION,
                    // source_map_enabled: true,
                    // guess_uncaught_frames: true
                }
            }
        }
    } : null,
    // devLogin: false,
    authUrl: `${B.MATERIALIZED_HOST}/auth`,
    apiUrl: `${B.BACKEND_HOST}/api`,
    adminUrl: `${B.BACKEND_HOST}/admin`,
    materializedApiUrl: `${B.MATERIALIZED_HOST}/api`,
    statusPageUrl: `${B.MATERIALIZED_HOST}/server-status`,
    apiDataIngestionUrl: `${B.INGESTION_HOST}/api`,
    sitApiUrl: `${B.MATERIALIZED_HOST}/api/sit`,
    staticFilesUrl: (B.DJANGO_GCP_STATIC_BUCKET
        ? `https://storage.googleapis.com/${B.DJANGO_GCP_STATIC_BUCKET}/static`
        : '') || 'http://localhost:8500/static',
    apiDelay: B.APP_IS_DEV ? 600 : 0,
    baseUrl: B.APP_URL,
    staticIo,
    lookerPreviewUrl: `${import.meta.env.VITE_LOOKER_PREVIEW_URL}`,
    demoFiles,
    aiVmName: 'ai-workhorse-6-eu-west4-a-1',
    fakePPVData: false,

    /**
     * Category names longer than this will be cut off with an ellipsis. Should be an even number
     * Example: "Hello my name...van den brand"
     * For Illumina customer this needs to be set (~to 36)
     */
    categoryNameCutoffEllipsisChars: 36,

    defaultPpvTablePageSize: 50,
    defaultPpvSubTablePageSize: 20,
    aiResultFakeTimeout: 2500,
    package: C.package,

    isTestReviewPage: false as (false | true | 'view-only'),
    isTestStaff: undefined as (boolean | undefined),

    isDemo: C.package === 'sales_demo',
    firebaseConfig,

    // Google Analytics enabled
    //   When Firebase is not loaded but we do send events to GA we will get the following error:
    //   "No Firebase App '[DEFAULT]' has been created - call Firebase App.initializeApp() (app/no-app)."
    isGA: Boolean(firebaseConfig.measurementId),

    hasAuth0: Boolean(B.AUTH0_CLIENT_ID),
    hasFrontegg: Boolean(B.FRONTEGG_CLIENT_ID),
    hasMultipleAuthProviders: Boolean(B.AUTH0_CLIENT_ID) && Boolean(B.FRONTEGG_CLIENT_ID),
    frontEggBaseUrl: B.FRONTEGG_DOMAIN ? `https://${B.FRONTEGG_DOMAIN}` : '',

    apiStatusPollingInterval: 30 * 1000,

    isUnderConstruction: (B.UNDER_CONSTRUCTION ? 'for-non-staff' : undefined) satisfies UnderConstructionOptions as UnderConstructionOptions,
};

//region Dennis Dev
if (B.APP_IS_DEV) {
    //
    environment.isTest = true;
    // environment.isTestChat = true;
    environment.apiStatusPollingInterval = 0;
    environment.isTestReviewPage = true;
    //
}
//endregion

export const TIMINGS = {
    'input_loading': [
        1, // Uploading
        15, // Parsing Excel
        7, // Optimizing Excel
        3, // Merging File(s)
    ], // seconds
}

if (!B.MATERIALIZED_HOST) {
    if (environment.production) {
        console.error('Misconfiguration error: Missing MATERIALIZED_HOST variable')
    } else {
        throw new Error('Misconfiguration error: Missing MATERIALIZED_HOST variable')
    }
}
if (!B.INGESTION_HOST) {
    if (environment.production) {
        console.error('Misconfiguration error: Missing INGESTION_HOST variable')
    } else {
        throw new Error('Misconfiguration error: Missing INGESTION_HOST variable')
    }
}

export const rollbar = environment.rollbarConfig ? new Rollbar(environment.rollbarConfig) : console;

const alreadySend = new Set<string>();

export function rollbarErrorOnce(msg: string, extra?: any) {
    if (!alreadySend.has(msg)) {
        alreadySend.add(msg);
        rollbar.error(msg, extra);
    }
}
